/**
 * IndexedDB Storage Layer
 * Persistent storage for conversations, messages, and settings
 */

const DB_NAME = 'chorus';
const DB_VERSION = 3;

let dbInstance = null;

function openDB() {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Conversations store
      if (!db.objectStoreNames.contains('conversations')) {
        const convStore = db.createObjectStore('conversations', { keyPath: 'id' });
        convStore.createIndex('updatedAt', 'updatedAt');
      }

      // Messages store (separate for performance with large conversations)
      if (!db.objectStoreNames.contains('messages')) {
        const msgStore = db.createObjectStore('messages', { keyPath: 'id' });
        msgStore.createIndex('conversationId', 'conversationId');
        msgStore.createIndex('timestamp', 'timestamp');
      }

      // Settings store
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }

      // Artifacts store
      if (!db.objectStoreNames.contains('artifacts')) {
        const artStore = db.createObjectStore('artifacts', { keyPath: 'id' });
        artStore.createIndex('conversationId', 'conversationId');
        artStore.createIndex('messageId', 'messageId');
      }

      // Documents store (Knowledge Base)
      if (!db.objectStoreNames.contains('documents')) {
        const docStore = db.createObjectStore('documents', { keyPath: 'id' });
        docStore.createIndex('uploadDate', 'uploadDate');
      }

      // Chunks store (document text chunks for RAG)
      if (!db.objectStoreNames.contains('chunks')) {
        const chunkStore = db.createObjectStore('chunks', { keyPath: 'id' });
        chunkStore.createIndex('documentId', 'documentId');
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
  });
}

function tx(storeName, mode = 'readonly') {
  return openDB().then(db => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    return { transaction, store };
  });
}

function promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ─────────────────────────────────────────
// CONVERSATIONS
// ─────────────────────────────────────────

export async function saveConversation(conversation) {
  const { store } = await tx('conversations', 'readwrite');
  // Store conversation metadata (without messages, those are separate)
  const { messages, ...meta } = conversation;
  await promisify(store.put(meta));

  // Save messages separately
  if (messages?.length) {
    const { store: msgStore } = await tx('messages', 'readwrite');
    for (const msg of messages) {
      await promisify(msgStore.put({ ...msg, conversationId: conversation.id }));
    }
  }
}

export async function loadConversations() {
  const { store } = await tx('conversations');
  const conversations = await promisify(store.getAll());

  // Load messages for each conversation
  for (const conv of conversations) {
    conv.messages = await loadMessages(conv.id);
  }

  // Sort by updatedAt descending
  conversations.sort((a, b) => b.updatedAt - a.updatedAt);
  return conversations;
}

export async function deleteConversation(id) {
  const { store } = await tx('conversations', 'readwrite');
  await promisify(store.delete(id));

  // Delete associated messages
  const { store: msgStore } = await tx('messages', 'readwrite');
  const index = msgStore.index('conversationId');
  const messages = await promisify(index.getAll(id));
  for (const msg of messages) {
    await promisify(msgStore.delete(msg.id));
  }
}

// ─────────────────────────────────────────
// MESSAGES
// ─────────────────────────────────────────

export async function saveMessage(message) {
  const { store } = await tx('messages', 'readwrite');
  await promisify(store.put(message));
}

export async function loadMessages(conversationId) {
  const { store } = await tx('messages');
  const index = store.index('conversationId');
  const messages = await promisify(index.getAll(conversationId));
  messages.sort((a, b) => a.timestamp - b.timestamp);
  return messages;
}

export async function updateMessageContent(messageId, content, status = 'complete') {
  const { store } = await tx('messages', 'readwrite');
  const msg = await promisify(store.get(messageId));
  if (msg) {
    msg.content = content;
    msg.status = status;
    await promisify(store.put(msg));
  }
}

// ─────────────────────────────────────────
// SETTINGS
// ─────────────────────────────────────────

export async function saveSetting(key, value) {
  const { store } = await tx('settings', 'readwrite');
  await promisify(store.put({ key, value }));
}

export async function loadSetting(key) {
  const { store } = await tx('settings');
  const result = await promisify(store.get(key));
  return result?.value;
}

export async function loadAllSettings() {
  const { store } = await tx('settings');
  const all = await promisify(store.getAll());
  const settings = {};
  for (const { key, value } of all) {
    settings[key] = value;
  }
  return settings;
}

// ─────────────────────────────────────────
// ARTIFACTS
// ─────────────────────────────────────────

export async function saveArtifact(artifact) {
  const { store } = await tx('artifacts', 'readwrite');
  await promisify(store.put(artifact));
}

export async function loadArtifacts(conversationId) {
  const { store } = await tx('artifacts');
  const index = store.index('conversationId');
  const artifacts = await promisify(index.getAll(conversationId));
  return artifacts;
}

export async function loadAllArtifacts() {
  const { store } = await tx('artifacts');
  return promisify(store.getAll());
}

export async function deleteArtifact(id) {
  const { store } = await tx('artifacts', 'readwrite');
  await promisify(store.delete(id));
}

export async function deleteArtifactsForConversation(conversationId) {
  const { store } = await tx('artifacts', 'readwrite');
  const index = store.index('conversationId');
  const artifacts = await promisify(index.getAll(conversationId));
  for (const art of artifacts) {
    await promisify(store.delete(art.id));
  }
}

// ─────────────────────────────────────────
// EXPORT/IMPORT
// ─────────────────────────────────────────

export async function exportData() {
  const conversations = await loadConversations();
  const settings = await loadAllSettings();
  return { conversations, settings, exportedAt: Date.now() };
}

export async function clearAll() {
  const db = await openDB();
  for (const name of db.objectStoreNames) {
    const { store } = await tx(name, 'readwrite');
    await promisify(store.clear());
  }
}

// ─────────────────────────────────────────
// DOCUMENTS (Knowledge Base)
// ─────────────────────────────────────────

export async function saveDocument(doc) {
  const { store } = await tx('documents', 'readwrite');
  await promisify(store.put(doc));
}

export async function getDocuments() {
  const { store } = await tx('documents');
  const docs = await promisify(store.getAll());
  docs.sort((a, b) => b.uploadDate - a.uploadDate);
  return docs;
}

export async function deleteDocument(docId) {
  const { store } = await tx('documents', 'readwrite');
  await promisify(store.delete(docId));
  // Delete associated chunks
  const { store: chunkStore } = await tx('chunks', 'readwrite');
  const index = chunkStore.index('documentId');
  const chunks = await promisify(index.getAll(docId));
  for (const chunk of chunks) {
    await promisify(chunkStore.delete(chunk.id));
  }
}

// ─────────────────────────────────────────
// CHUNKS (Document text segments for RAG)
// ─────────────────────────────────────────

export async function saveChunks(chunks) {
  const { store } = await tx('chunks', 'readwrite');
  for (const chunk of chunks) {
    await promisify(store.put(chunk));
  }
}

export async function getDocumentChunks(docId) {
  const { store } = await tx('chunks');
  const index = store.index('documentId');
  const chunks = await promisify(index.getAll(docId));
  chunks.sort((a, b) => a.index - b.index);
  return chunks;
}

/**
 * TF-IDF style search across chunks
 * @param {string} query - Search query
 * @param {string[]} docIds - Document IDs to search within
 * @param {number} topK - Number of results to return
 * @returns {Promise<Array<{chunk: Object, score: number}>>}
 */
export async function searchChunks(query, docIds, topK = 5) {
  if (!query.trim() || !docIds.length) return [];

  const queryTerms = tokenize(query);
  if (!queryTerms.length) return [];

  // Load all chunks for the specified documents
  const allChunks = [];
  for (const docId of docIds) {
    const chunks = await getDocumentChunks(docId);
    allChunks.push(...chunks);
  }

  if (!allChunks.length) return [];

  // Compute IDF for query terms across all chunks
  const docCount = allChunks.length;
  const idf = {};
  for (const term of queryTerms) {
    const docsWithTerm = allChunks.filter(c => c.keywords?.[term]).length;
    idf[term] = docsWithTerm > 0 ? Math.log(docCount / docsWithTerm) + 1 : 0;
  }

  // Score each chunk by TF-IDF
  const scored = allChunks.map(chunk => {
    let score = 0;
    for (const term of queryTerms) {
      const tf = chunk.keywords?.[term] || 0;
      score += tf * (idf[term] || 0);
    }
    return { chunk, score };
  });

  // Sort by score descending and return top K
  scored.sort((a, b) => b.score - a.score);
  return scored.filter(s => s.score > 0).slice(0, topK);
}

/**
 * Tokenize text into normalized terms
 */
function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 2);
}

export const db = {
  saveConversation,
  loadConversations,
  deleteConversation,
  saveMessage,
  loadMessages,
  updateMessageContent,
  saveSetting,
  loadSetting,
  loadAllSettings,
  saveArtifact,
  loadArtifacts,
  loadAllArtifacts,
  deleteArtifact,
  deleteArtifactsForConversation,
  exportData,
  clearAll,
  saveDocument,
  getDocuments,
  deleteDocument,
  saveChunks,
  getDocumentChunks,
  searchChunks,
};
